import { config } from 'config'
import { Language, MappingsType, Person, PersonRaw, StringKeys } from 'models'

type Mappings<T, V> = {
  [l in Language]: Partial<{
    [k in keyof V]: keyof T
  }>
}

const MAPPINGS: Mappings<PersonRaw, Person> = {
  ua: {
    additional_info: 'Additional info UA',
    burial_place: 'Burial place UA',
    formation_of_the_raf: 'Formation of the RAF UA',
    name: 'Name UA',
    place_of_death: 'Place of Death UA',
    place_of_origin: 'Place of origin UA',
    subregion: 'Subregion ↓',
    citizenship: 'Citizenship ↓ ',
    military: 'Military ↓ ',
  },
  ru: {
    additional_info: 'Additional info RU',
    burial_place: 'Burial place RU',
    formation_of_the_raf: 'Formation of the RAF RU',
    name: 'Name RU',
    place_of_death: 'Place of Death RU',
    place_of_origin: 'Place of origin RU',
    subregion: 'Subregion ↓',
    citizenship: 'Citizenship ↓ ',
    military: 'Military ↓ ',
  },
}

const normalizeKeys = (key: string) =>
  key
    .toLowerCase()
    .replaceAll(/[^a-z0-9 ]/g, '')
    .trim()
    .replaceAll(/[\s]/g, '_') as keyof Person

const removeProps = (object: PersonRaw, propsToOmit: string[]) => {
  return Object.keys(object)
    .filter(key => !propsToOmit.includes(key))
    .reduce((obj, key) => {
      obj[normalizeKeys(key)] = object[key as keyof PersonRaw]
      return obj
    }, {} as Partial<Person>)
}

// Region: string
// RegionRU: string
// Military: string
// MiltaryRU: string
// Citizenship: string
// CitizenshipRU: string
// Subregion: string
// 'Subregion RU': string
// 'Region RU': string

const mapProps = (
  row: Partial<Person>,
  enums: MappingsType[]
): Partial<Person> => ({
  ...row,
  region: (enums.find(e => e.Subregion === row.subregion) ?? {})[
    config.language === 'ru' ? 'Region RU' : 'Region'
  ],
  region_map: enums.find(e => e.Subregion === row.subregion)?.Region,
  citizenship: (enums.find(e => e.Citizenship === row.citizenship) ?? {})[
    config.language === 'ru' ? 'CitizenshipRU' : 'Citizenship'
  ],
  military: (enums.find(e => e.Military === row.military) ?? {})[
    config.language === 'ru' ? 'MiltaryRU' : 'Military'
  ],
})

export const localizeColumns = (
  data: PersonRaw[],
  enums: MappingsType[]
): Person[] => {
  const mappings = MAPPINGS[config.language]
  const propsToOmit = Object.keys(MAPPINGS)
    .map(key => MAPPINGS[key as keyof typeof MAPPINGS])
    .map(lang => Object.keys(lang).map(key => lang[key as keyof typeof lang]))
    .flat()

  return data
    .map((row, index) => {
      const mapped = Object.keys(mappings).reduce(
        (acc, key) => ({
          ...acc,
          [key as keyof Person]:
            row[mappings[key as keyof Person] as keyof PersonRaw],
        }),
        {
          id: (index + 1).toString(),
        } as Partial<{ [k in keyof Person]: keyof PersonRaw }>
      )

      return {
        ...removeProps(row, propsToOmit as string[]),
        ...mapProps(mapped, enums),
        id: (index + 1).toString(),
      }
    })
    .map(item => {
      if (
        !item.date_of_death ||
        !/[0-9]{1,2}\.\s?[0-9]{1,2}\.\s?[0-9]{4}/.test(item.date_of_death)
      ) {
        return {
          ...item,
          date_of_death: config.language === 'ru' ? 'Неизвестно' : 'Невідомо',
        }
      }

      return item
    }) as unknown as Person[]
}

type RawStrings = Array<{
  'String name (do not modify)': string
  UA: string
  RU: string
  Placeholder: string
}>

export const processStrings = (array: RawStrings) => {
  const output = array.reduce(
    (acc, row) => ({
      ...acc,
      [row['String name (do not modify)']]:
        row[config.language.toUpperCase() as keyof typeof row],
    }),
    {} as { [k in StringKeys]: string }
  )
  return output
}
